import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Chip, IconButton, InputBase, Paper, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search } from '@mui/icons-material';
import { compact, isEmpty, values } from 'lodash';

import useInfoDialog from 'common/hooks/useInfoDialog';
import {
  resetShipstationFilter,
  updateShipstationFilter,
} from '../../../store/thunks/shipstationShipmentThunks';
import ShipstationFilterButton from './ShipstationFilterButton';

const ShipstationShipmentToolbar = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { filter, count } = useSelector(({ shipstationShipment }) => shipstationShipment.list);
  const { InfoDialog, showInfoDialog } = useInfoDialog();

  const handleSearch = useCallback(
    (event) => {
      event.preventDefault();
      const searchText = event.target.elements.search.value.trim();

      if (searchText?.length < 3) {
        showInfoDialog({ title: 'Error', message: 'Minimum 3 characters needed for search' });
        return;
      }

      dispatch(updateShipstationFilter({ filter: { flatSearch: searchText } }));
    },
    [dispatch, showInfoDialog]
  );

  const removeFilter = useCallback(
    (filterName) => () => {
      dispatch(updateShipstationFilter({ filter: { [filterName]: undefined } }));
    },
    [dispatch]
  );

  const removeAllFilters = useCallback(() => {
    dispatch(resetShipstationFilter());
  }, [dispatch]);

  const hasActiveFilters = !isEmpty(compact(values(filter)));

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <InfoDialog />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Filter chips */}
        <Box sx={{ flexGrow: 1, minWidth: 0, mr: 'auto' }}>
          <Stack direction='row' flexWrap='wrap' alignItems='center' rowGap={0.5} spacing={0.5}>
            {filter.flatSearch && (
              <Chip
                size='small'
                label={`Search = ${filter.flatSearch}`}
                variant='contained'
                onDelete={removeFilter('flatSearch')}
              />
            )}
            {filter.createdAfter && (
              <Chip
                size='small'
                label={`From = ${filter.createdAfter}`}
                variant='contained'
                onDelete={removeFilter('createdAfter')}
              />
            )}
            {filter.createdBefore && (
              <Chip
                size='small'
                label={`To = ${filter.createdBefore}`}
                variant='contained'
                onDelete={removeFilter('createdBefore')}
              />
            )}
            {filter.orderId && (
              <Chip
                size='small'
                label={`Order ID = ${filter.orderId}`}
                variant='contained'
                onDelete={removeFilter('orderId')}
              />
            )}
            {filter.trackingNumber && (
              <Chip
                size='small'
                label={`Tracking = ${filter.trackingNumber}`}
                variant='contained'
                onDelete={removeFilter('trackingNumber')}
              />
            )}
            {!isEmpty(filter.partnerIds) && (
              <Chip
                size='small'
                label={`Partners (${filter.partnerIds.length})`}
                variant='contained'
                onDelete={removeFilter('partnerIds')}
              />
            )}
            {!isEmpty(filter.practiceIds) && (
              <Chip
                size='small'
                label={`Practices (${filter.practiceIds.length})`}
                variant='contained'
                onDelete={removeFilter('practiceIds')}
              />
            )}
            {hasActiveFilters && (
              <Button size='small' onClick={removeAllFilters}>
                Remove All
              </Button>
            )}
            <Chip
              size='small'
              label={`Total: ${count}`}
              color='primary'
              sx={{ color: '#FFFFFF' }}
            />
          </Stack>
        </Box>

        {/* Search bar */}
        <Box sx={{ flexShrink: 0 }}>
          <Paper
            component='form'
            sx={{ display: 'flex', alignItems: 'center', width: 280 }}
            onSubmit={handleSearch}
          >
            <InputBase
              inputProps={{ autoComplete: 'off', 'aria-label': 'search' }}
              sx={{ border: 'none', px: theme.spacing(2), flex: 1 }}
              type='text'
              placeholder='Search...'
              name='search'
              defaultValue={filter?.flatSearch || ''}
            />
            <IconButton type='submit' aria-label='search'>
              <Search />
            </IconButton>
          </Paper>
        </Box>

        {/* Filter button */}
        <Box sx={{ flexShrink: 0 }}>
          <ShipstationFilterButton />
        </Box>
      </Box>
    </Box>
  );
};

export default ShipstationShipmentToolbar;
